import * as m from '@staiter/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { confirmEmailChange } from '~/lib/api/auth';
import { ApiRequestError } from '~/lib/api/client';
import styles from './reset-password.module.css';

const SearchSchema = z.object({ token: z.string().min(1).optional() });

export const Route = createFileRoute('/confirm-email-change')({
  validateSearch: SearchSchema,
  component: ConfirmEmailChangePage,
});

type State = 'pending' | 'success' | 'invalid' | 'no_token' | 'network';

function ConfirmEmailChangePage(): JSX.Element {
  const { token } = Route.useSearch();
  const [state, setState] = useState<State>(token ? 'pending' : 'no_token');
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    confirmEmailChange(token)
      .then(() => setState('success'))
      .catch((err) => {
        if (err instanceof ApiRequestError) {
          setState(err.status === 400 || err.status === 409 ? 'invalid' : 'network');
        } else {
          setState('network');
        }
      });
  }, [token]);

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <div className={styles.brand} aria-hidden="true">
          <img src="/favicon.svg" alt="" width={24} height={24} className={styles.brandLogo} />
          STAITER
        </div>

        {state === 'pending' && (
          <>
            <h1 className={styles.title}>{m.ui_podtverzhdaem_novyy_email()}</h1>
            <p className={styles.lede}>{m.ui_sekundu_primenyaem_smenu_adresa()}</p>
          </>
        )}

        {state === 'success' && (
          <>
            <h1 className={styles.title}>{m.ui_email_izmenen()}</h1>
            <p className={styles.lede}>
              {m.ui_novyy_adres_podtverzhden_voydite_zanovo_ispolzuya_novyy_e()}
            </p>
            <a href="/login" className={styles.link}>
              {m.ui_pereyti_ko_vhodu()}
            </a>
          </>
        )}

        {(state === 'invalid' || state === 'no_token') && (
          <div className={styles.fatal}>
            <h1 className={styles.fatalTitle}>{m.ui_ssylka_nedeystvitelna()}</h1>
            <p className={styles.fatalBody}>
              Ссылка устарела или уже использована. Запросите смену email заново в настройках
              профиля.
            </p>
            <a href="/login" className={styles.link}>
              {m.ui_pereyti_ko_vhodu()}
            </a>
          </div>
        )}

        {state === 'network' && (
          <div className={styles.fatal}>
            <h1 className={styles.fatalTitle}>{m.ui_ne_udalos_podtverdit()}</h1>
            <p className={styles.fatalBody}>
              {m.ui_oshibka_seti_otkroyte_ssylku_iz_pisma_esche_raz()}
            </p>
            <a href="/login" className={styles.link}>
              {m.ui_pereyti_ko_vhodu()}
            </a>
          </div>
        )}
      </section>
    </main>
  );
}
